// Purchase-scenario explanation contract shared by the worksheet and the Edge
// Function. The purchasing engine owns every number; AI is allowed to improve
// the prose only. This module keeps the deterministic fallback and the output
// guard identical on both sides of the API boundary.

export const PURCHASE_SCENARIO_EXPLANATION_SCHEMA = Object.freeze({
  name: "purchase_scenario_explanation",
  strict: true,
  schema: {
    type: "object",
    additionalProperties: false,
    properties: {
      explanation: { type: "string", minLength: 20, maxLength: 1600 },
    },
    required: ["explanation"],
  },
});

const finiteOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const cleanText = (value, fallback = "") => {
  const text = typeof value === "string" ? value.trim() : "";
  return text || fallback;
};

const cleanStringList = (values) => [...new Set(
  (Array.isArray(values) ? values : [])
    .map((value) => cleanText(value))
    .filter(Boolean),
)];

/**
 * Resolve the received-quantity PAIR in one basis. The objective figures are
 * in the comparison unit; the fallback figures are in the count unit. Mixing
 * one of each fabricates a delta, so a pair resolves together or not at all.
 */
const resolveReceivedPair = (change) => {
  const directPrev = finiteOrNull(change.previousQuantityReceived);
  const directScen = finiteOrNull(change.scenarioQuantityReceived);
  if (directPrev !== null && directScen !== null) {
    return { previous: directPrev, scenario: directScen, basis: change.comparisonUnit ? "comparison" : "count" };
  }
  const objPrev = finiteOrNull(change.fromObjectiveQuantityReceived);
  const objScen = finiteOrNull(change.toObjectiveQuantityReceived);
  if (objPrev !== null && objScen !== null) {
    return { previous: objPrev, scenario: objScen, basis: "comparison" };
  }
  const countPrev = finiteOrNull(change.fromQuantityReceived);
  const countScen = finiteOrNull(change.toQuantityReceived);
  if (countPrev !== null && countScen !== null) {
    return { previous: countPrev, scenario: countScen, basis: "count" };
  }
  return { previous: null, scenario: null, basis: null };
};

// NOTE: used as `.map(cleanChange)` — never give this a second parameter, or
// Array.prototype.map's index argument silently fills it.
const cleanChange = (change = {}) => {
  const receivedPair = resolveReceivedPair(change);
  const cleaned = {
  product: cleanText(change.product || change.description, "Item"),
  previousVendor: cleanText(change.previousVendor || change.fromVendor, "Unassigned"),
  scenarioVendor: cleanText(change.scenarioVendor || change.toVendor) || null,
  previousOrderCost: finiteOrNull(change.previousOrderCost ?? change.fromOrderCost),
  scenarioOrderCost: finiteOrNull(change.scenarioOrderCost ?? change.toOrderCost),
  spendDelta: finiteOrNull(change.spendDelta ?? change.itemCostDelta),
  needQuantity: finiteOrNull(change.needQuantity),
  needUnit: cleanText(change.needUnit) || null,
  comparisonUnit: cleanText(change.comparisonUnit) || null,
  previousComparableUnitCost: finiteOrNull(
    change.previousComparableUnitCost ?? change.fromComparableUnitCost,
  ),
  scenarioComparableUnitCost: finiteOrNull(
    change.scenarioComparableUnitCost ?? change.toComparableUnitCost,
  ),
  comparableUnitCostDelta: finiteOrNull(change.comparableUnitCostDelta),
  comparisonEstimated: change.comparisonEstimated === true,
  // Resolved TOGETHER in one basis (see resolveReceivedPair) — the delta and
  // its unit label must never subtract a comparison-unit figure from a
  // count-unit figure.
  previousQuantityReceived: receivedPair.previous,
  scenarioQuantityReceived: receivedPair.scenario,
  quantityBasis: receivedPair.basis,
  quantityReceivedDelta: finiteOrNull(
    change.quantityReceivedDelta ?? change.objectiveQuantityDelta,
  ),
  previousPackageQuantity: finiteOrNull(
    change.previousPackageQuantity ?? change.fromObjectiveQuantityPerPackage,
  ),
  scenarioPackageQuantity: finiteOrNull(
    change.scenarioPackageQuantity ?? change.toObjectiveQuantityPerPackage,
  ),
  previousPurchaseUnit: cleanText(change.previousPurchaseUnit || change.fromPurchaseUnit) || null,
  scenarioPurchaseUnit: cleanText(change.scenarioPurchaseUnit || change.toPurchaseUnit) || null,
  endingVsPar: finiteOrNull(change.endingVsPar),
  reachesPar: change.reachesPar === true ? true : change.reachesPar === false ? false : null,
  // Equivalent-quantity price facts. grossPriceImpact = (previous unit price
  // − scenario unit price) × the scenario's delivered comparable quantity;
  // positive = gross price savings, negative = gross price premium. Null
  // whenever the two options share no verified comparison unit — a purchase
  // total difference between unlike quantities is never a price claim.
  grossPriceImpact: finiteOrNull(change.grossPriceImpact),
  equivalentQuantityCostAtPreviousUnitPrice: finiteOrNull(
    change.equivalentQuantityCostAtPreviousUnitPrice,
  ),
  // PAR provenance, when the count rows still carry it. Best effort — the
  // sentence degrades to the need alone when these are absent.
  parLevel: finiteOrNull(change.parLevel),
  countedOnHand: finiteOrNull(change.countedOnHand),
  parSource: cleanText(change.parSource) || null,
  reason: cleanText(change.reason) || null,
  };
  // Unit prices in operator language, computed here so the model copies a
  // phrase instead of verbalizing an eight-decimal quotient. They exist only
  // when the change carries a verified comparison unit AND the engine trusts
  // the comparison — comparePurchasePlans nulls comparableUnitCostDelta for
  // an untrustworthy pairing, and recomputing it from the raw costs here
  // would hand the model a confident price claim the worksheet simultaneously
  // labels "does not support a winner". Absent flag = trusted, for
  // hand-built inputs.
  const unit = cleaned.comparisonUnit;
  const unitPriceTrusted = change.unitPriceComparisonTrustworthy !== false;
  cleaned.previousUnitPriceText = unit && unitPriceTrusted
    ? unitPriceText(cleaned.previousComparableUnitCost, unit)
    : null;
  cleaned.scenarioUnitPriceText = unit && unitPriceTrusted
    ? unitPriceText(cleaned.scenarioComparableUnitCost, unit)
    : null;
  const unitDelta = cleaned.comparableUnitCostDelta;
  cleaned.unitPriceChangeText = unit && unitPriceTrusted
    && unitDelta !== null && Math.abs(unitDelta) >= 5e-7
    ? `${unitPriceAmountText(Math.abs(unitDelta))} ${unitDelta > 0 ? "more" : "less"} per ${unit}`
    : null;
  return cleaned;
};

export function normalizePurchaseScenarioFacts(input = {}) {
  const coverageStatus = input.coverageStatus === "complete" ? "complete" : "incomplete";
  const landedTotalStatus = input.landedTotalStatus === "complete" ? "complete" : "incomplete";
  const changes = (Array.isArray(input.changes) ? input.changes : []).map(cleanChange);
  const unresolvedItems = (Array.isArray(input.unresolvedItems) ? input.unresolvedItems : [])
    .map(cleanChange)
    .map((item) => ({ ...item, spendDelta: null }));

  return {
    scenarioName: cleanText(input.scenarioName, "Current scenario"),
    excludedVendors: cleanStringList(input.excludedVendors),
    coverageStatus,
    coveredProductCount: Math.max(0, Math.round(finiteOrNull(input.coveredProductCount) ?? 0)),
    requiredProductCount: Math.max(0, Math.round(finiteOrNull(input.requiredProductCount) ?? 0)),
    coveredProductSpendDelta: finiteOrNull(input.coveredProductSpendDelta),
    missingBaselineSpend: finiteOrNull(input.missingBaselineSpend),
    overallSavings: coverageStatus === "complete" ? finiteOrNull(input.overallSavings) : null,
    // Purchase total change (scenario − recommended, landed): the cash fact,
    // named apart from savings because paying less for less product is not a
    // saving. Negative = the scenario costs less at checkout.
    purchaseTotalDelta: coverageStatus === "complete"
      ? finiteOrNull(input.purchaseTotalDelta)
      : null,
    quantityChangedProductCount: Math.max(
      0,
      Math.round(finiteOrNull(input.quantityChangedProductCount) ?? 0),
    ),
    grossPriceImpactTotal: finiteOrNull(input.grossPriceImpactTotal),
    grossPriceImpactComplete: input.grossPriceImpactComplete === true,
    comparableScenarioTotal: coverageStatus === "complete"
      ? finiteOrNull(input.comparableScenarioTotal)
      : null,
    partialAssignedSubtotal: finiteOrNull(input.partialAssignedSubtotal),
    landedTotalStatus,
    recommendedArrangement: {
      vendors: cleanStringList(input.recommendedArrangement?.vendors),
      vendorCount: Math.max(0, Math.round(finiteOrNull(input.recommendedArrangement?.vendorCount) ?? 0)),
      pickupStops: Math.max(0, Math.round(finiteOrNull(input.recommendedArrangement?.pickupStops) ?? 0)),
      landedTotal: finiteOrNull(input.recommendedArrangement?.landedTotal),
    },
    scenarioArrangement: {
      vendors: cleanStringList(input.scenarioArrangement?.vendors),
      vendorCount: Math.max(0, Math.round(finiteOrNull(input.scenarioArrangement?.vendorCount) ?? 0)),
      pickupStops: Math.max(0, Math.round(finiteOrNull(input.scenarioArrangement?.pickupStops) ?? 0)),
      landedTotal: coverageStatus === "complete"
        ? finiteOrNull(input.scenarioArrangement?.landedTotal)
        : null,
    },
    changes,
    unresolvedItems,
  };
}

const dollars = (value) => {
  const amount = finiteOrNull(value);
  if (amount === null) return "unavailable";
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

/**
 * A per-unit amount in operator language. Two-decimal dollars flatten every
 * sub-dime price into "$0.01" — or "$0.00" for a sub-cent difference — while
 * the raw quotient ($0.010625/napkin) is unreadable. Below a dime the amount
 * is said in cents: "about 1.1 cents", "about 9/10ths of a cent", "less than
 * 1/10th of a cent". At a dime and above, ordinary two-decimal dollars.
 */
export function unitPriceAmountText(value) {
  const amount = finiteOrNull(value);
  if (amount === null) return null;
  const abs = Math.abs(amount);
  if (abs >= 0.1 || abs === 0) return dollars(abs);
  if (abs >= 0.0095) {
    const cents = Math.round(abs * 1000) / 10;
    return `about ${cents} cent${cents === 1 ? "" : "s"}`;
  }
  if (abs >= 0.0005) {
    const tenths = Math.round(abs * 1000);
    return tenths === 1
      ? "about 1/10th of a cent"
      : `about ${tenths}/10ths of a cent`;
  }
  return "less than 1/10th of a cent";
}

/** The amount with its unit — "about 1.1 cents per ct", "$0.85 per lb". */
export function unitPriceText(value, unitLabel) {
  const base = unitPriceAmountText(value);
  if (base === null) return null;
  return unitLabel ? `${base} per ${unitLabel}` : base;
}

const joinNames = (names = []) => {
  if (names.length <= 1) return names[0] || "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

const countWord = (count) => {
  const words = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
  return words[count] || String(count);
};

const deltaPhrase = (delta) => {
  const amount = finiteOrNull(delta);
  if (amount === null) return "cannot be compared yet";
  if (Math.abs(amount) < 0.005) return "is unchanged";
  return amount < 0 ? `is ${dollars(-amount)} lower` : `is ${dollars(amount)} higher`;
};

const quantityAmount = (value, unit) => {
  const amount = finiteOrNull(value);
  if (amount === null) return null;
  const rounded = Math.round(amount * 1000) / 1000;
  return unit ? `${rounded} ${unit}` : String(rounded);
};

/**
 * One compact clause describing why a single moved product changes the money:
 * the cash fact (purchase total), the quantity fact (what is received), and —
 * only when the two options share a verified comparison unit — the price fact
 * at equivalent quantities. The three never collapse into one signed dollar.
 */
const describeMove = (change) => {
  const route = change.scenarioVendor
    ? `${change.product} moves from ${change.previousVendor} to ${change.scenarioVendor}`
    : `${change.product} leaves ${change.previousVendor}`;
  const parts = [];
  if (change.spendDelta !== null) {
    parts.push(`its purchase total ${deltaPhrase(change.spendDelta)}`);
  }
  // Quantities and their unit come from the SAME basis, resolved as a pair by
  // cleanChange: comparison-unit figures wear comparisonUnit, count-unit
  // fallbacks wear the need unit, and a mixed pair resolves to nothing.
  const qtyDelta = change.scenarioQuantityReceived !== null
    && change.previousQuantityReceived !== null
    ? change.scenarioQuantityReceived - change.previousQuantityReceived
    : null;
  const unit = change.quantityBasis === "comparison"
    ? change.comparisonUnit
    : change.needUnit;
  if (qtyDelta !== null && Math.abs(qtyDelta) > 0.0005) {
    // Package sizes are comparison-basis facts; only pair them with a
    // comparison-basis quantity sentence.
    const packagePhrase = change.quantityBasis === "comparison"
      && change.scenarioPackageQuantity !== null
      && change.previousPackageQuantity !== null && unit
      ? ` (${quantityAmount(change.scenarioPackageQuantity, unit)} per ${change.scenarioPurchaseUnit || "package"} instead of ${quantityAmount(change.previousPackageQuantity, unit)})`
      : "";
    parts.push(`you receive ${quantityAmount(Math.abs(qtyDelta), unit)} ${qtyDelta < 0 ? "less" : "more"}${packagePhrase}`);
  } else if (qtyDelta !== null) {
    parts.push(`you receive the same quantity`);
  }
  const gross = finiteOrNull(change.grossPriceImpact);
  if (gross !== null && Math.abs(gross) >= 0.005) {
    // The per-unit difference in cents language, when it exists — the reader
    // thinks "2/10ths of a cent per napkin", never "$0.0019".
    const unitPhrase = change.unitPriceChangeText ? ` (${change.unitPriceChangeText})` : "";
    parts.push(gross > 0
      ? `at the same quantity that is a genuine gross price saving of ${dollars(gross)}${unitPhrase}`
      : `at the same quantity that is a gross price premium of ${dollars(-gross)}${unitPhrase}`);
  }
  return parts.length ? `${route}: ${parts.join(", ")}.` : `${route}.`;
};

export function composeDeterministicPurchaseScenarioExplanation(input = {}) {
  const facts = normalizePurchaseScenarioFacts(input);
  const sentences = [];
  const previousVendors = facts.recommendedArrangement.vendors;
  if (previousVendors.length) {
    sentences.push(`The recommended arrangement uses ${joinNames(previousVendors)}.`);
  }

  const comparableMoves = facts.changes.filter((change) => change.spendDelta !== null);
  const purchaseTotalDelta = facts.purchaseTotalDelta
    ?? (facts.scenarioArrangement.landedTotal !== null
      && facts.recommendedArrangement.landedTotal !== null
      ? Math.round((facts.scenarioArrangement.landedTotal - facts.recommendedArrangement.landedTotal) * 100) / 100
      : null);
  if (facts.coverageStatus === "complete" && purchaseTotalDelta !== null) {
    sentences.push(`This arrangement's purchase total ${deltaPhrase(purchaseTotalDelta)}${facts.scenarioArrangement.landedTotal !== null && facts.recommendedArrangement.landedTotal !== null ? ` (${dollars(facts.scenarioArrangement.landedTotal)} instead of ${dollars(facts.recommendedArrangement.landedTotal)})` : ""}.`);
  } else if (comparableMoves.length) {
    sentences.push(
      `${countWord(comparableMoves.length)} product${comparableMoves.length === 1 ? "" : "s"} moved, and the combined purchase total for them ${deltaPhrase(facts.coveredProductSpendDelta)}.`,
    );
  }

  // The movers that actually explain the money, largest cash change first.
  const movers = [...comparableMoves]
    .sort((a, b) => Math.abs(b.spendDelta ?? 0) - Math.abs(a.spendDelta ?? 0))
    .slice(0, 3);
  movers.forEach((change) => sentences.push(describeMove(change)));

  // The operational consequence in operator words: how many suppliers are
  // ordered from, and how many pickup trips the arrangement demands. Only
  // stated when the scenario actually changes one of them.
  const vendorCountDelta = facts.scenarioArrangement.vendorCount
    - facts.recommendedArrangement.vendorCount;
  const pickupStopDelta = facts.scenarioArrangement.pickupStops
    - facts.recommendedArrangement.pickupStops;
  const operational = [];
  if (vendorCountDelta === -1) operational.push("uses one fewer vendor");
  else if (vendorCountDelta < -1) operational.push(`uses ${countWord(-vendorCountDelta).toLowerCase()} fewer vendors`);
  else if (vendorCountDelta === 1) operational.push("adds another vendor to order from");
  else if (vendorCountDelta > 1) operational.push(`adds ${countWord(vendorCountDelta).toLowerCase()} more vendors to order from`);
  if (pickupStopDelta === 1) operational.push("adds a pickup stop");
  else if (pickupStopDelta > 1) operational.push(`adds ${countWord(pickupStopDelta).toLowerCase()} pickup stops`);
  else if (pickupStopDelta === -1) operational.push("removes a pickup stop");
  else if (pickupStopDelta < -1) operational.push(`removes ${countWord(-pickupStopDelta).toLowerCase()} pickup stops`);
  if (operational.length) {
    sentences.push(`This arrangement ${operational.join(" and ")}.`);
  }

  // A cheaper checkout that buys less product must say so in plain words —
  // but ONLY when a quantity actually dropped. A fee-driven lower total with
  // identical quantities is a fee fact, and misattributing it to "buying less
  // product" would be its own false claim.
  if (purchaseTotalDelta !== null && purchaseTotalDelta < -0.004
      && facts.grossPriceImpactTotal !== null && facts.grossPriceImpactTotal < -0.004
      && facts.quantityChangedProductCount > 0) {
    sentences.push("The lower total comes from buying less product, not from better prices — it is not a price saving, and keeping the recommended arrangement may be the better value.");
  } else if (facts.grossPriceImpactComplete && facts.grossPriceImpactTotal !== null
      && Math.abs(facts.grossPriceImpactTotal) >= 0.005) {
    sentences.push(facts.grossPriceImpactTotal > 0
      ? `Compared at the same quantities, this arrangement is a genuine gross price saving of ${dollars(facts.grossPriceImpactTotal)} before fees and tax.`
      : `Compared at the same quantities, this arrangement pays a gross price premium of ${dollars(-facts.grossPriceImpactTotal)} before fees and tax.`);
  }

  if (facts.unresolvedItems.length) {
    const names = joinNames(facts.unresolvedItems.map((item) => item.product));
    const priorVendors = cleanStringList(facts.unresolvedItems.map((item) => item.previousVendor));
    const keepVendor = priorVendors.length === 1 ? priorVendors[0] : "the previous supplier";
    sentences.push(
      `${names} ${facts.unresolvedItems.length === 1 ? "does" : "do"} not have an approved replacement, so ${dollars(facts.missingBaselineSpend)} from the recommended order is missing—not saved. Keep ${keepVendor} for ${facts.unresolvedItems.length === 1 ? "this item" : "these items"}, or choose another approved supplier.`,
    );
  }

  if (facts.coverageStatus === "incomplete" || facts.landedTotalStatus === "incomplete") {
    sentences.push("The scenario is incomplete, so the purchase-total comparison and the landed total are unavailable until every required product reaches PAR with a valid package quantity and price.");
  }
  return sentences.join(" ");
}

export function buildPurchaseScenarioSystemPrompt() {
  return [
    "You explain vendor-allocation changes in a restaurant purchase plan, so the operator understands what is really moving the money without reading every line.",
    "The supplied JSON is authoritative. Improve the wording only; never recalculate, infer, or replace a value.",
    "Write 3-6 short, conversational sentences for a restaurant operator. Lead with the one-sentence takeaway, then explain the cause.",
    "Use these terms with exactly these meanings, and never blur them:",
    "- Purchase total: the cash actually paid for the packages in the plan.",
    "- Purchase total change: how much more or less cash this scenario requires (purchaseTotalDelta; per item, spendDelta). Lower is 'lower', never automatically 'savings'.",
    "- Quantity difference: how much more or less product is received (the quantityReceived and packageQuantity fields).",
    "- Gross price savings / gross price premium: each change's grossPriceImpact, which compares both choices over the same quantity, before fees and tax. Positive = savings, negative = premium. Only these fields may be called savings or a better/worse price.",
    "Unit prices below a dime are said in cents language, never as long decimals. Each change supplies ready phrases — previousUnitPriceText, scenarioUnitPriceText, unitPriceChangeText — use them as written ('about 1.1 cents per napkin instead of about 9/10ths of a cent', 'about 2/10ths of a cent more per napkin'). You may swap the generic unit word in a phrase for the product's natural unit noun (napkin, straw, piece). Never write a dollar amount with more than two decimal places, such as $0.010625.",
    "Name the one or two products most responsible for the movement and say WHY the money moves: usually a different pack size at a different unit price. Example shape: 'Orange Juice drives most of it — the replacement comes in a 1.219 gal case instead of a 4 gal case, so you spend $15.96 less but receive 2.781 gal less at a higher unit price.'",
    "When the purchase total is lower but grossPriceImpact is negative or the quantity received drops, say plainly that the plan spends less because it buys less product, that this is not a price saving, and that keeping the original arrangement may be the better value.",
    "When grossPriceImpact is null for a change, make no price comparison for it — the products have no reliable common unit.",
    "When the scenario changes the vendor count or the pickup stops (compare recommendedArrangement with scenarioArrangement), state the operational change in plain words — 'adds a Costco pickup stop', 'one fewer vendor to order from'. Never invent a change those fields do not show.",
    "PAR is the first constraint. A valid reassignment may be described as reaching PAR only when its reachesPar field is true. When parLevel and countedOnHand are supplied, you may cite them (PAR minus counted stock is the need); mention parSource when present.",
    "If coverageStatus is incomplete: say the scenario is incomplete; say missing baseline spend is not savings; do not state a purchase-total comparison or landed total; and explain that the previous vendor must be kept for the unresolved item or another approved replacement selected.",
    "Do not call a null price zero. Do not call an unassigned item purchased. Do not claim the whole plan reaches PAR when any item is unresolved.",
    "Return only the requested JSON object.",
  ].join("\n");
}

export function buildPurchaseScenarioUserPrompt(input = {}) {
  const facts = normalizePurchaseScenarioFacts(input);
  return [
    "Explain the effects of this purchase-allocation scenario using every relevant supplied fact.",
    "Use the calculated amounts exactly as provided.",
    JSON.stringify(facts, null, 2),
  ].join("\n\n");
}

const currencyAmountsIn = (text) => [...String(text).matchAll(/\$\s*([\d,]+(?:\.\d{1,2})?)/g)]
  .map((match) => Number(match[1].replace(/,/g, "")))
  .filter(Number.isFinite);

const allowedCurrencyAmounts = (facts) => {
  const values = new Set();
  const add = (value) => {
    const amount = finiteOrNull(value);
    if (amount !== null) values.add(Math.round(Math.abs(amount) * 100) / 100);
  };
  add(facts.coveredProductSpendDelta);
  add(facts.missingBaselineSpend);
  add(facts.overallSavings);
  add(facts.purchaseTotalDelta);
  add(facts.grossPriceImpactTotal);
  add(facts.comparableScenarioTotal);
  add(facts.partialAssignedSubtotal);
  add(facts.recommendedArrangement.landedTotal);
  add(facts.scenarioArrangement.landedTotal);
  [...facts.changes, ...facts.unresolvedItems].forEach((change) => {
    add(change.previousOrderCost);
    add(change.scenarioOrderCost);
    add(change.spendDelta);
    add(change.previousComparableUnitCost);
    add(change.scenarioComparableUnitCost);
    add(change.comparableUnitCostDelta);
    add(change.grossPriceImpact);
    add(change.equivalentQuantityCostAtPreviousUnitPrice);
  });
  return values;
};

export function validatePurchaseScenarioExplanation(explanation, input = {}) {
  const text = cleanText(explanation);
  if (!text || text.length > 1600) return null;
  const facts = normalizePurchaseScenarioFacts(input);
  const allowed = allowedCurrencyAmounts(facts);
  const inventedAmount = currencyAmountsIn(text)
    .some((amount) => !allowed.has(Math.round(Math.abs(amount) * 100) / 100));
  if (inventedAmount) return null;
  // Long-decimal dollar strings ($0.010625) are exactly what the unit-price
  // phrases exist to replace. An explanation that still prints one is
  // rejected; the deterministic fallback never produces more than two
  // decimal places after a dollar sign.
  if (/\$\s*\d[\d,]*\.\d{3,}/.test(text)) return null;

  if (facts.coverageStatus === "incomplete") {
    if (!/\bincomplete\b/i.test(text)) return null;
    if (facts.missingBaselineSpend > 0 && !/\bnot (?:a )?savings?\b|\bnot saved\b/i.test(text)) return null;
    if (/\b(?:overall|total|scenario|plan|order)\s+(?:saves?|savings)\s+\$/i.test(text)
      || /\boverall savings\s+(?:is|are|of)\s+\$/i.test(text)) return null;
    const missing = dollars(facts.missingBaselineSpend).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    if (facts.missingBaselineSpend > 0) {
      const calledSavings = new RegExp(`(?:save|saving|savings|less)[^.]{0,45}${missing}|${missing}[^.]{0,45}(?:save|saving|savings|less)`, "i");
      if (calledSavings.test(text) && !/not (?:a )?savings?|not saved/i.test(text)) return null;
    }
    if (/\b(?:scenario|plan|order)\s+(?:still\s+)?reaches?\s+PAR\b/i.test(text)) return null;
    if (facts.unresolvedItems.some((item) => !text.toLowerCase().includes(item.product.toLowerCase()))) return null;
  }

  // A lower purchase total may be worded as savings ONLY when the
  // equivalent-quantity comparison is complete and genuinely positive. In
  // every other lower-total case — gross premium, partial comparison, or no
  // verified common unit at all — savings wording must carry the tradeoff (or
  // its own negation). The deterministic fallback always passes this gate.
  if (facts.coverageStatus === "complete"
    && facts.purchaseTotalDelta !== null && facts.purchaseTotalDelta < -0.004) {
    const genuinePriceSavings = facts.grossPriceImpactComplete
      && facts.grossPriceImpactTotal !== null && facts.grossPriceImpactTotal > 0.004;
    if (!genuinePriceSavings) {
      const claimsSavings = /\bsav(?:e|es|ed|ing|ings)\b/i.test(text);
      // 'fewer' alone is NOT an acknowledgment — "fewer pickup stops" says
      // nothing about product received. The list is product-quantity, price,
      // and negation phrases only.
      const acknowledgesTradeoff = /premium|less product|receives? less|buy(?:s|ing)? less|smaller (?:pack|case|quantit)|not (?:a )?(?:price )?saving|no (?:\w+ )?savings?\b|isn['’]t (?:a )?saving|not saved/i.test(text);
      if (claimsSavings && !acknowledgesTradeoff) return null;
    }
  }
  return text;
}

export function buildPurchaseScenarioDiscussionPrompt(input = {}) {
  const facts = normalizePurchaseScenarioFacts(input);
  const unresolved = facts.unresolvedItems.map((item) => item.product);
  // Only a COMPLETE equivalent-quantity comparison earns a price sentence in
  // the seeded discussion — a partial sum dominated by unknown lines is not a
  // price fact the conversation should anchor on.
  const grossPhrase = !facts.grossPriceImpactComplete || facts.grossPriceImpactTotal === null
    ? ""
    : facts.grossPriceImpactTotal >= 0
      ? ` Compared at the same quantities, the arrangement is a gross price saving of ${dollars(facts.grossPriceImpactTotal)} before fees and tax.`
      : ` Compared at the same quantities, the arrangement pays a gross price premium of ${dollars(-facts.grossPriceImpactTotal)} before fees and tax.`;
  return [
    `Help me decide how to arrange the purchase order for the “${facts.scenarioName}” scenario.`,
    `Keep PAR coverage first and use these calculated facts: ${facts.coveredProductCount} of ${facts.requiredProductCount} products are covered; the purchase total for the moved products ${deltaPhrase(facts.coveredProductSpendDelta)}; quantities change for ${facts.quantityChangedProductCount} product${facts.quantityChangedProductCount === 1 ? "" : "s"}; ${dollars(facts.missingBaselineSpend)} from the recommended plan is missing and is not savings.${grossPhrase}`,
    unresolved.length
      ? `The unresolved ${unresolved.length === 1 ? "item is" : "items are"} ${joinNames(unresolved)}. Compare keeping ${joinNames(cleanStringList(facts.unresolvedItems.map((item) => item.previousVendor)))} for ${unresolved.length === 1 ? "that item" : "those items"} with choosing another approved supplier.`
      : "Compare the vendor arrangement, quantity differences, pickup stops, fees, and complete landed total — a lower purchase total that buys less product is not a saving.",
    "Do not recalculate the supplied amounts. Ask me one useful follow-up question if a vendor preference or operational constraint would change the recommendation.",
  ].join(" ");
}
